import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Box,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { uploadPatisserieImageToS3 } from "../../../utils/uploadToS3";
import api from "../../../services/api";

const PatisserieFormModal = ({ onClose, onSave, patisserieItem }) => {
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    description: "",
    searchableDescription: "",
    category: "patisserie",
    subcategory: "",
    flavor: "",
    images: { regularOrderImage: "", weddingPackageImage: "" },
    availability: { regularOrder: true, weddingPackage: false },
    orderConfig: {
      regular: { minSet: 1, itemsPerSet: 1, price: "" },
      wedding: { minSet: "", itemsPerSet: "", price: "" },
    },
    isGlutenFree: false,
    ingredients: [],
    allergens: [],
    keywords: [],
    seo: { metaTitle: "", metaDescription: "", metaKeywords: [] },
    notes: "",
    isActive: true,
    isAvailable: true,
  });

  const [regularImageFile, setRegularImageFile] = useState(null);
  const [weddingImageFile, setWeddingImageFile] = useState(null);
  const [regularImagePreview, setRegularImagePreview] = useState("");
  const [weddingImagePreview, setWeddingImagePreview] = useState("");
  const [uploading, setUploading] = useState(false);

  const allergensOptions = ["Nuts", "Dairy", "Eggs", "Gluten", "Soy", "Sesame"];
  const subcategoryOptions = [
    "cookie",
    "cupcake",
    "cheesecakeSlice",
    "cakeSlice",
    "pastry",
    "macaron",
  ];

  useEffect(() => {
    if (patisserieItem) {
      setFormData({
        ...patisserieItem,
        searchableDescription: patisserieItem.searchableDescription || "",
        keywords: patisserieItem.keywords || [],
        notes: patisserieItem.notes || "",
        isActive: patisserieItem.isActive ?? true,
        isAvailable: patisserieItem.isAvailable ?? true,
      });
      setRegularImagePreview(patisserieItem.images?.regularOrderImage || "");
      setWeddingImagePreview(patisserieItem.images?.weddingPackageImage || "");
    }
  }, [patisserieItem]);

  const handleRegularImageChange = (e) => {
    const file = e.target.files[0];
    setRegularImageFile(file);
    setRegularImagePreview(URL.createObjectURL(file));
  };

  const handleWeddingImageChange = (e) => {
    const file = e.target.files[0];
    setWeddingImageFile(file);
    setWeddingImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    try {
      setUploading(true);
      let regularOrderImageUrl = formData.images.regularOrderImage;
      let weddingPackageImageUrl = formData.images.weddingPackageImage;

      if (regularImageFile) {
        const uploadedRegular = await uploadPatisserieImageToS3(
          regularImageFile,
          formData.name,
          "regular"
        );
        regularOrderImageUrl = uploadedRegular?.fileUrl;
      }

      if (weddingImageFile) {
        const uploadedWedding = await uploadPatisserieImageToS3(
          weddingImageFile,
          formData.name,
          "wedding"
        );
        weddingPackageImageUrl = uploadedWedding?.fileUrl;
      }

      const payload = {
        ...formData,
        images: {
          regularOrderImage: regularOrderImageUrl,
          weddingPackageImage: weddingPackageImageUrl,
        },
        orderConfig: {
          regular: {
            minSet: Number(formData.orderConfig.regular.minSet),
            itemsPerSet: Number(formData.orderConfig.regular.itemsPerSet),
            price: Number(formData.orderConfig.regular.price),
          },
          wedding: formData.orderConfig.wedding.minSet
            ? {
                minSet: Number(formData.orderConfig.wedding.minSet),
                itemsPerSet: Number(formData.orderConfig.wedding.itemsPerSet),
                price: Number(formData.orderConfig.wedding.price),
              }
            : null,
        },
      };

      if (patisserieItem) {
        await api.put(`/patisserie/${patisserieItem._id}`, payload);
      } else {
        await api.post("/patisserie", payload);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error("Error uploading or saving patisserie item:", error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {patisserieItem ? "Edit Patisserie Item" : "Add New Patisserie Item"}
      </DialogTitle>
      <DialogContent>
        <TextField
          margin="dense"
          label="Internal Name"
          fullWidth
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
        <TextField
          margin="dense"
          label="Display Name"
          fullWidth
          value={formData.displayName}
          onChange={(e) =>
            setFormData({ ...formData, displayName: e.target.value })
          }
          required
        />
        <TextField
          margin="dense"
          label="Description"
          fullWidth
          multiline
          rows={2}
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          required
        />
        <TextField
          margin="dense"
          label="Searchable Description"
          fullWidth
          value={formData.searchableDescription}
          onChange={(e) =>
            setFormData({ ...formData, searchableDescription: e.target.value })
          }
        />
        <FormControl fullWidth margin="dense">
          <InputLabel>Subcategory</InputLabel>
          <Select
            value={formData.subcategory}
            onChange={(e) =>
              setFormData({ ...formData, subcategory: e.target.value })
            }>
            {subcategoryOptions.map((sub) => (
              <MenuItem key={sub} value={sub}>
                {sub}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          margin="dense"
          label="Flavor"
          fullWidth
          value={formData.flavor}
          onChange={(e) => setFormData({ ...formData, flavor: e.target.value })}
        />
        <TextField
          margin="dense"
          label="Regular Min Order Set"
          type="number"
          fullWidth
          value={formData.orderConfig.regular.minSet}
          onChange={(e) =>
            setFormData({
              ...formData,
              orderConfig: {
                ...formData.orderConfig,
                regular: {
                  ...formData.orderConfig.regular,
                  minSet: e.target.value,
                },
              },
            })
          }
        />
        <TextField
          margin="dense"
          label="Wedding Min Order Set"
          type="number"
          fullWidth
          value={formData.orderConfig.wedding.minSet}
          onChange={(e) =>
            setFormData({
              ...formData,
              orderConfig: {
                ...formData.orderConfig,
                wedding: {
                  ...formData.orderConfig.wedding,
                  minSet: e.target.value,
                },
              },
            })
          }
        />
        <TextField
          margin="dense"
          label="Regular Items Per Set"
          type="number"
          fullWidth
          value={formData.orderConfig.regular.itemsPerSet}
          onChange={(e) =>
            setFormData({
              ...formData,
              orderConfig: {
                ...formData.orderConfig,
                regular: {
                  ...formData.orderConfig.regular,
                  itemsPerSet: e.target.value,
                },
              },
            })
          }
        />
        <TextField
          margin="dense"
          label="Wedding Items Per Set"
          type="number"
          fullWidth
          value={formData.orderConfig.wedding.itemsPerSet}
          onChange={(e) =>
            setFormData({
              ...formData,
              orderConfig: {
                ...formData.orderConfig,
                wedding: {
                  ...formData.orderConfig.wedding,
                  itemsPerSet: e.target.value,
                },
              },
            })
          }
        />
        <TextField
          margin="dense"
          label="Regular Order Price"
          type="number"
          fullWidth
          value={formData.orderConfig.regular.price}
          onChange={(e) =>
            setFormData({
              ...formData,
              orderConfig: {
                ...formData.orderConfig,
                regular: {
                  ...formData.orderConfig.regular,
                  price: e.target.value,
                },
              },
            })
          }
        />
        <TextField
          margin="dense"
          label="Wedding Order Price"
          type="number"
          fullWidth
          value={formData.orderConfig.wedding.price}
          onChange={(e) =>
            setFormData({
              ...formData,
              orderConfig: {
                ...formData.orderConfig,
                wedding: {
                  ...formData.orderConfig.wedding,
                  price: e.target.value,
                },
              },
            })
          }
        />
        <Box sx={{ display: "flex", gap: 4, alignItems: "center", mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({ ...formData, isActive: e.target.checked })
                }
              />
            }
            label="Visible to Customers"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isAvailable}
                onChange={(e) =>
                  setFormData({ ...formData, isAvailable: e.target.checked })
                }
              />
            }
            label="Available for Ordering"
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <strong>Available for:</strong>
          <Switch
            checked={formData.availability.regularOrder}
            onChange={(e) =>
              setFormData({
                ...formData,
                availability: {
                  ...formData.availability,
                  regularOrder: e.target.checked,
                },
              })
            }
          />{" "}
          Regular Orders
          <Switch
            checked={formData.availability.weddingPackage}
            onChange={(e) =>
              setFormData({
                ...formData,
                availability: {
                  ...formData.availability,
                  weddingPackage: e.target.checked,
                },
              })
            }
          />{" "}
          Wedding Packages
        </Box>
        <TextField
          margin="dense"
          label="Internal Notes"
          fullWidth
          multiline
          rows={2}
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        />
        <TextField
          margin="dense"
          label="Ingredients (comma-separated)"
          fullWidth
          value={formData.ingredients.join(", ")}
          onChange={(e) =>
            setFormData({
              ...formData,
              ingredients: e.target.value.split(",").map((i) => i.trim()),
            })
          }
        />
        <FormControl fullWidth margin="dense">
          <InputLabel>Allergens</InputLabel>
          <Select
            multiple
            value={formData.allergens}
            onChange={(e) =>
              setFormData({ ...formData, allergens: e.target.value })
            }
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((allergen) => (
                  <Chip key={allergen} label={allergen} />
                ))}
              </Box>
            )}>
            {allergensOptions.map((allergen) => (
              <MenuItem key={allergen} value={allergen}>
                {allergen}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          margin="dense"
          label="Keywords (comma-separated)"
          fullWidth
          value={formData.keywords.join(", ")}
          onChange={(e) =>
            setFormData({
              ...formData,
              keywords: e.target.value.split(",").map((k) => k.trim()),
            })
          }
        />
        <TextField
          margin="dense"
          label="SEO Meta Title"
          fullWidth
          value={formData.seo.metaTitle}
          onChange={(e) =>
            setFormData({
              ...formData,
              seo: { ...formData.seo, metaTitle: e.target.value },
            })
          }
        />
        <TextField
          margin="dense"
          label="SEO Meta Description"
          fullWidth
          value={formData.seo.metaDescription}
          onChange={(e) =>
            setFormData({
              ...formData,
              seo: { ...formData.seo, metaDescription: e.target.value },
            })
          }
        />
        <TextField
          margin="dense"
          label="SEO Meta Keywords (comma-separated)"
          fullWidth
          value={formData.seo.metaKeywords.join(", ")}
          onChange={(e) =>
            setFormData({
              ...formData,
              seo: {
                ...formData.seo,
                metaKeywords: e.target.value.split(",").map((k) => k.trim()),
              },
            })
          }
        />
        <Box sx={{ mt: 2 }}>
          <strong>Regular Order Image:</strong>
          <input
            type="file"
            accept="image/*"
            onChange={handleRegularImageChange}
          />
          {regularImagePreview && (
            <img
              src={regularImagePreview}
              alt="Regular Order Preview"
              style={{ width: 100, marginLeft: 10, borderRadius: 8 }}
            />
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          <strong>Wedding Package Image:</strong>
          <input
            type="file"
            accept="image/*"
            onChange={handleWeddingImageChange}
          />
          {weddingImagePreview && (
            <img
              src={weddingImagePreview}
              alt="Wedding Package Preview"
              style={{ width: 100, marginLeft: 10, borderRadius: 8 }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={uploading}>
          {uploading ? "Uploading..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatisserieFormModal;
